import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard } from 'react-native';

import { TabRootListFallback, TabRootScrollView } from '@/components/TabRootList';
import { ContentLibraryItemCard } from '@/components/contentLibrary/contentLibraryItemCard';
import { FilterSortSelectInput } from '@/components/contentLibrary/filterSortSelectInput';
import { PageWebBanner } from '@/components/pageWebBanner';
import { Pressable } from '@/components/ui/pressable';
import { SearchField } from '@/components/ui/search-field';
import { SelectItem } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { GENERAL_EMAIL_ADDRESS, GENERAL_EMAIL_LINK } from '@/constants/links';
import { useAbility } from '@/hooks/useAbility';
import { useEmailLink } from '@/hooks/useEmailLink';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getContentLibraryItemsV2QueryOptions } from '@/services/getContentLibraryItemsV2';
import { type ContentLibraryItem } from '@/types/contentLibraryItems';
import { segmentClient } from '@/util/analytics';
import {
  getFilteredContentLibraryItems,
  getSortedContentLibraryItems,
} from '@/utils/contentLibraryHelpers';

const selectAllCancerType = {
  id: 0,
  label: 'Filter by cancer type',
  shortLabel: null,
  value: 'Filter by cancer type',
};

const sortByOptions = [
  {
    id: 'newest',
    name: 'Sort by newest',
  },
  {
    id: 'mostLiked',
    name: 'Sort by most liked',
  },
];

const DEFAULT_SORT = 'newest';

export function ContentLibrary() {
  const hasTrackedView = useRef(false);

  const { openEmailLink } = useEmailLink();

  const [search, setSearch] = useState('');

  const [selectedCancerType, setSelectedCancerType] = useState(0);

  const [selectedSortBy, setSelectedSortBy] = useState(DEFAULT_SORT);

  const [filteredAndSortedContentLibraryItems, setFilteredAndSortedContentLibraryItems] = useState<
    ContentLibraryItem[] | undefined
  >(undefined);

  const contentLibraryItemsQuery = useQuery(getContentLibraryItemsV2QueryOptions());

  const contentLibraryItems = contentLibraryItemsQuery.data?.contentLibraryItems;

  const cancerTypesQuery = useQuery(getCancerTypesV2QueryOptions());

  const cancerTypes = cancerTypesQuery.data?.cancerTypes
    ? [selectAllCancerType, ...cancerTypesQuery.data.cancerTypes]
    : undefined;

  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const noResultsCopy = useMemo(() => {
    if (search) {
      return `No results found for "${search}". Try adjusting your search.`;
    }

    if (selectedCancerType) {
      return 'No items match the selected filter. Try removing the filter.';
    }

    return 'There are currently no Content Library items to display.';
  }, [search, selectedCancerType]);

  useEffect(() => {
    if (contentLibraryItems) {
      setFilteredAndSortedContentLibraryItems(
        getSortedContentLibraryItems(selectedSortBy, contentLibraryItems)
      );
    }
  }, [contentLibraryItems]);

  useEffect(() => {
    if (contentLibraryItems) {
      if (search.trim() || selectedCancerType) {
        setFilteredAndSortedContentLibraryItems(
          getSortedContentLibraryItems(
            selectedSortBy,
            getFilteredContentLibraryItems(search, selectedCancerType, [...contentLibraryItems])
          )
        );
      } else {
        setFilteredAndSortedContentLibraryItems(
          getSortedContentLibraryItems(selectedSortBy, [...contentLibraryItems])
        );
      }
    }
  }, [search, selectedCancerType, selectedSortBy, contentLibraryItems]);

  // On native, Content Library is a section inside `Library` that stays mounted once visited,
  // rather than its own routed screen, so `useFocusEffect` here would fire on every
  // Library screen focus regardless of which tab is active. Native view-tracking
  // fires from `Library`'s tab-index-change handler instead; this stays web-only.
  useFocusEffect(
    useCallback(() => {
      if (isWeb && !contentLibraryItemsQuery.isLoading && !hasTrackedView.current) {
        hasTrackedView.current = true;

        Promise.resolve(segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_VIEWED)).catch(
          () => undefined
        );
      }

      return () => {
        hasTrackedView.current = false;
      };
    }, [contentLibraryItemsQuery.isLoading])
  );

  if (contentLibraryItemsQuery.isLoading) {
    return (
      <TabRootListFallback>
        <Spinner className="py-24" size="large" />
      </TabRootListFallback>
    );
  }

  return (
    <>
      <TabRootScrollView className="no-scrollbar" showsVerticalScrollIndicator={false}>
        {isWeb ? (
          <PageWebBanner
            header="Real-world oncology insights, on demand"
            subtext={
              <Text className="text-center">
                <Text className="text-white">
                  Curated content shaped by clinical practice and your needs. Email{' '}
                </Text>
                <Pressable
                  className="inline"
                  onPress={() => void openEmailLink(GENERAL_EMAIL_LINK)}>
                  <Text className="text-white underline">{GENERAL_EMAIL_ADDRESS}</Text>
                </Pressable>
                <Text className="text-white"> to share content.</Text>
              </Text>
            }
          />
        ) : null}
        <View className="mx-auto w-full max-w-screen-xl px-4 lg:px-6">
          <View className="grid grid-cols-2 gap-4 py-4 sm:grid-cols-4 md:py-8">
            <SearchField
              label="Search content"
              placeholder="Search by keyword"
              value={search}
              onChangeText={setSearch}
              onSubmitEditing={() => Keyboard.dismiss()}
              className="col-span-2"
            />
            <FilterSortSelectInput
              onValueChange={(id) => setSelectedCancerType(Number(id))}
              options={
                (cancerTypes &&
                  cancerTypes.map(({ id, label, value }) => (
                    <SelectItem
                      key={String(id)}
                      label={id === 0 ? label || value : label}
                      value={String(id)}
                    />
                  ))) ||
                []
              }
              placeholder="Cancer type"
              selectedValue={String(selectedCancerType)}
              value={
                selectedCancerType === 0
                  ? selectAllCancerType.label
                  : cancerTypes?.find((cancerType) => cancerType.id === selectedCancerType)
                      ?.label || ''
              }
            />
            <FilterSortSelectInput
              onValueChange={(id) => setSelectedSortBy(id)}
              options={sortByOptions.map(({ id, name }) => (
                <SelectItem key={id} label={name} value={id} />
              ))}
              placeholder="Sort by"
              selectedValue={String(selectedSortBy)}
              value={
                sortByOptions.find((sortByOption) => sortByOption.id === selectedSortBy)?.name || ''
              }
            />
          </View>
          {filteredAndSortedContentLibraryItems && filteredAndSortedContentLibraryItems.length ? (
            <View className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredAndSortedContentLibraryItems.map((contentLibraryItem) => {
                return (
                  <ContentLibraryItemCard
                    contentLibraryItem={contentLibraryItem}
                    key={contentLibraryItem.id}
                    search={search}
                  />
                );
              })}
            </View>
          ) : (
            <Text className="py-6 text-center">{noResultsCopy}</Text>
          )}
        </View>
      </TabRootScrollView>
    </>
  );
}
